import type { SEDAmendment } from '../../types/sedAmendments';
import { createRegistryResolver, tierSource } from './resolveCore';
import type { RegistryResolver, ResolverFactoryOptions } from './types';

export interface AmendmentResolverInput extends ResolverFactoryOptions {
  amendments?: SEDAmendment[];
  providerValues?: Record<string, unknown>;
  llmValues?: Record<string, unknown>;
}

const ACTIVE_AMENDMENT_STATUSES = new Set(['applied', 'approved', 'pending']);

export function createAmendmentResolver(opts: AmendmentResolverInput): RegistryResolver {
  const fieldSources: Record<string, ReturnType<typeof tierSource>[]> = {};

  for (const [path, value] of Object.entries(opts.providerValues ?? {})) {
    if (value === undefined || value === null) continue;
    fieldSources[path] = [
      tierSource(1, value, {
        kind: 'provider',
        ref: `provider:${path}`,
        label: 'Provider baseline',
      }),
    ];
  }

  const activeAmendments = (opts.amendments ?? []).filter((a) =>
    ACTIVE_AMENDMENT_STATUSES.has(a.status),
  );
  for (const amendment of activeAmendments) {
    const sources = fieldSources[amendment.variablePath] ?? [];
    sources.unshift(
      tierSource(1, amendment.newValue, {
        kind: 'user-amendment',
        ref: `amendment:${amendment.id}`,
        label: amendment.variableLabel,
      }),
    );
    fieldSources[amendment.variablePath] = sources;
  }

  for (const [path, value] of Object.entries(opts.llmValues ?? {})) {
    if (value === undefined || value === null) continue;
    const sources = fieldSources[path] ?? [];
    sources.push(
      tierSource(3, value, {
        kind: 'llm-inference',
        ref: `llm:amendment:${path}`,
        label: 'LLM suggested value',
      }),
    );
    fieldSources[path] = sources;
  }

  return createRegistryResolver({
    registryId: 'amendment',
    siteId: opts.siteId,
    fieldSources,
    now: opts.now,
  });
}
